/**
 * Sales — the lead and target pipelines (REQ-017).
 *
 * One component, two routes. Inbound leads and outbound targets are the same
 * record with different provenance, so showing them through two divergent
 * screens would be two places to fix the same bug.
 *
 * What the screen has to make visible, because REQ-017 puts the burden on being
 * able to show it: where each record came from, on what legal basis, whether a
 * named human reviewed it, and whether it may be written to at all. A row whose
 * provenance cannot be read is a row that cannot be defended.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  Check,
  Inbox,
  Loader2,
  Search,
  ShieldAlert,
  Target,
  X,
} from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

type Kind = "inbound" | "outbound";
type ReviewState = "candidate" | "relevant" | "rejected";

interface Lead {
  id: string;
  kind: Kind;
  email: string;
  isRoleAddress: boolean;
  organisation?: string;
  contactName?: string;
  contactRole?: string;
  country?: string;
  website?: string;
  organisationType?: string;
  provenance: {
    source: string;
    sourceHost?: string;
    collectedAt: string;
    legalBasis: "consent" | "legitimate_interest";
    consentWording?: string;
  };
  review: { state: ReviewState; by?: string; at?: string; reason?: string };
  requiresPriorConsent: boolean;
  contactable: { ok: boolean; because?: string };
}

const BASIS_LABEL = {
  consent: "Consent",
  legitimate_interest: "Legitimate interest",
} as const;

const STATE_STYLE: Record<ReviewState, string> = {
  candidate: "bg-amber-100 text-amber-800",
  relevant: "bg-emerald-100 text-emerald-800",
  rejected: "bg-gray-100 text-gray-500",
};

const STATE_LABEL: Record<ReviewState, string> = {
  candidate: "Awaiting review",
  relevant: "Relevant",
  rejected: "Rejected",
};

export default function LeadPipeline({ kind }: { kind: Kind }) {
  const fmt = useLocaleFormat();
  const [search, setSearch] = useState("");
  const [state, setState] = useState<ReviewState | "">("");
  const queryClient = useQueryClient();

  const key = ["crm", "leads", kind, search, state];

  const leads = useQuery({
    queryKey: key,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({ kind });
      if (search.trim().length > 1) params.set("q", search.trim());
      if (state) params.set("state", state);
      const response = await apiClient.get<{
        data: { leads: Lead[] };
        meta: { total: number };
      }>(`/staff/leads?${params.toString()}`);
      return response.data;
    },
  });

  const review = useMutation({
    mutationFn: async (vars: {
      id: string;
      state: "relevant" | "rejected";
    }) => {
      await apiClient.post(`/staff/leads/${vars.id}/review`, {
        state: vars.state,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const suppressAddress = useMutation({
    mutationFn: async (email: string) => {
      await apiClient.post("/staff/leads/suppress", {
        value: email,
        reason: "Asked to stop",
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const Icon = kind === "inbound" ? Inbox : Target;
  const title = kind === "inbound" ? "Lead Pipeline" : "Target Pipeline";
  const subtitle =
    kind === "inbound"
      ? "People who gave us their address — a demo booking, a form, a sign-up. Consent, recorded and revocable."
      : "Organisations found in public professional sources. Legitimate interest, and nothing is written to before a named person reviews it.";

  return (
    <div className="p-6">
      <header className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Icon className="h-5 w-5 text-sky-600" /> {title}
            {leads.data && (
              <span className="text-sm font-normal text-gray-500">
                {leads.data.meta.total}
              </span>
            )}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Address or organisation"
                className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none"
              />
            </label>
            <select
              value={state}
              onChange={(event) =>
                setState(event.target.value as ReviewState | "")
              }
              className="rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-sky-500 focus:outline-none"
            >
              <option value="">Every state</option>
              <option value="candidate">Awaiting review</option>
              <option value="relevant">Relevant</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-gray-600">{subtitle}</p>
      </header>

      {leads.isError && (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>The pipeline could not be read.</span>
        </div>
      )}

      {leads.isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {leads.data?.data.leads.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          {kind === "inbound"
            ? "No inbound leads yet. Demo bookings and sign-ups land here once they are recorded as leads."
            : "No targets yet. Collection runs against configured sources are not built; targets can be added one at a time through the API in the meantime."}
        </div>
      )}

      {leads.data && leads.data.data.leads.length > 0 && (
        <div className="space-y-3">
          {leads.data.data.leads.map((lead) => (
            <article
              key={lead.id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {lead.organisation ?? lead.contactName ?? lead.email}
                  </p>
                  <p className="truncate text-sm text-gray-600">
                    {lead.email}
                    {lead.isRoleAddress && (
                      <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                        role address
                      </span>
                    )}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLE[lead.review.state]}`}
                >
                  {STATE_LABEL[lead.review.state]}
                </span>
              </div>

              {/* Provenance is shown, not stored quietly: being able to show it
                  is what the legitimate-interest balance rests on. */}
              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="text-gray-400">Source</dt>
                  <dd>{lead.provenance.source}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-gray-400">Collected</dt>
                  <dd>{fmt.date(lead.provenance.collectedAt)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-gray-400">Basis</dt>
                  <dd>{BASIS_LABEL[lead.provenance.legalBasis]}</dd>
                </div>
                {lead.review.by && (
                  <div className="flex gap-2">
                    <dt className="text-gray-400">Reviewed by</dt>
                    <dd>
                      {lead.review.by}
                      {lead.review.at && `, ${fmt.date(lead.review.at)}`}
                    </dd>
                  </div>
                )}
              </dl>

              {lead.requiresPriorConsent &&
                lead.provenance.legalBasis !== "consent" && (
                  <p className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    German address. German unfair-competition law effectively
                    requires prior consent for commercial email, so the balance
                    struck elsewhere in the EU does not carry here.
                  </p>
                )}

              {!lead.contactable.ok && !lead.requiresPriorConsent && (
                <p className="mt-3 text-xs text-gray-500">
                  Not contactable — {lead.contactable.because}
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {lead.review.state === "candidate" && (
                  <>
                    <button
                      onClick={() =>
                        review.mutate({ id: lead.id, state: "relevant" })
                      }
                      disabled={review.isPending}
                      className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Check className="h-3.5 w-3.5" /> Relevant
                    </button>
                    <button
                      onClick={() =>
                        review.mutate({ id: lead.id, state: "rejected" })
                      }
                      disabled={review.isPending}
                      className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => suppressAddress.mutate(lead.email)}
                  disabled={suppressAddress.isPending}
                  title="Records an opt-out for this address. It outlives the lead record and cannot be undone by re-collection."
                  className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Ban className="h-3.5 w-3.5" /> Suppress
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
